import {
  AddToCalendarConfigKeys,
  InteractionAppNames,
} from '@/types/gql.generated';

type InteractionAppLike = {
  applicationName: InteractionAppNames;
  config: { setting: string; value: string }[];
};

type ExperienceLike = {
  interactionApps: InteractionAppLike[];
};

/**
 * Whether this app cascades onto the child views a planner renders its pages
 * through, carrying its config and its per-event slots with it. Off is an author
 * who wants the interaction reachable (platform bar, event modal) without a
 * control on every card in the page.
 *
 * Absent reads as inherited rather than off: the value arrives merged over the
 * app's server-side defaults, so a missing row is an app answered before the
 * setting existed or a config assembled client-side (the magic preview builds
 * its own), and every one of those cascaded.
 *
 * The setting is declared by the Add to Calendar app, the only interaction with
 * a config today, but nothing here is specific to it: an app that never declares
 * the setting simply keeps cascading.
 */
export const isInteractionAppInherited = (app: InteractionAppLike): boolean =>
  app.config.find(
    (config) => config.setting === AddToCalendarConfigKeys.IsInherited
  )?.value !== 'false';

/**
 * Whether the experience passes Add to Calendar down to its child views, i.e.
 * what the Schedule-settings "Include controls in page" toggle reports. An
 * experience with no such app answers true, the value the toggle would write
 * back if it were switched on.
 */
export const isExperienceAddToCalendarInherited = (
  experience: ExperienceLike
): boolean => {
  const app = experience.interactionApps.find(
    (item) => item.applicationName === InteractionAppNames.AddToCalendar
  );

  return app ? isInteractionAppInherited(app) : true;
};
